import StateObserver, { now, user } from 'src/StateObserver';
import { track } from 'src/lib/analytics/utils';
import { GCInstant as platform } from 'src/lib/gcinstant';
import {
  PopupDialogOrigin,
  openPopupPromise,
} from 'src/lib/popups/popupOpenClose';
import { captureGenericError } from 'src/lib/sentry';
import { Feature } from 'src/replicant/analytics';
import {
  getPromoShownCounter,
  isPromoReady,
} from 'src/replicant/promo/promo.getters';
import {
  getHomeShortcutGemsReward,
  isHomeShortcutEnabled,
} from './homeShortcut.utils';
import { withLoading } from 'src/lib/utils';

export async function homeShortcutLaunchSequence() {
  const { entryData } = platform;

  // Grant launch reward if launched from home shortcut and if not already claimed
  if (
    entryData?.$channel === 'HOMESCREEN' &&
    isPromoReady(user(), 'homeShortcutLaunch', now())
  ) {
    // Show the reward popup if there are rewards to claim
    if (getHomeShortcutGemsReward('launch') > 0) {
      await openPopupPromise('popupHomeShortcutReward', {
        type: 'launch',
        origin: 'launch',
      });

      // Claim the reward
      await StateObserver.invoke.claimPromoRewards({
        id: 'homeShortcutLaunch',
        analytics: {
          feature: Feature.HOME_SHORTCUT._,
          $subFeature: Feature.HOME_SHORTCUT.LAUNCH,
        },
      });
    } else {
      // There are no rewards to claim, just mark as shown
      await StateObserver.invoke.promoWasShown('homeShortcutLaunch');
    }

    // At this point, the feature is complete, so we can ignore the rest of the sequence
    return;
  }

  // Skip showing the main popup on launch sequence if already shown 3 times in user lifetime
  if (getPromoShownCounter(user(), 'homeShortcutPopupAtLaunch') >= 3) return;

  // Skip showing the main popup on launch sequence if still on cooldown
  if (!isPromoReady(user(), 'homeShortcutPopupAtLaunch', now())) return;

  // Show the main popup
  await openHomeShortcutPopup('launch');

  // Trigger the cooldown for showing the main popup at launch again
  await StateObserver.invoke.promoWasShown('homeShortcutPopupAtLaunch');
}

export async function openHomeShortcutPopup(origin: PopupDialogOrigin) {
  if (!isHomeShortcutEnabled()) return;
  await openPopupPromise('popupHomeShortcut', {
    origin,
  });
}

export async function addHomeShortcutSequence(origin: PopupDialogOrigin) {
  // Can only run this sequence if the feature is enabled
  if (!isHomeShortcutEnabled()) {
    return false;
  }

  // Attempt to add the home shortcut with loading and analytics
  const added = await withLoading(() => addHomeShortcut(origin), {
    feature: Feature.HOME_SHORTCUT._,
    subFeature: Feature.HOME_SHORTCUT.ADD,
  });

  // If successfully added, resolve promo if ready
  if (added && isPromoReady(user(), 'homeShortcutAdd', now())) {
    // Show the reward popup if there are rewards to claim
    if (getHomeShortcutGemsReward('add') > 0) {
      await openPopupPromise('popupHomeShortcutReward', {
        type: 'add',
        origin,
      });

      await StateObserver.invoke.claimPromoRewards({
        id: 'homeShortcutAdd',
        analytics: {
          feature: Feature.HOME_SHORTCUT._,
          $subFeature: Feature.HOME_SHORTCUT.ADD,
        },
      });
    } else {
      // No rewards to claim, just mark as shown
      await StateObserver.invoke.promoWasShown('homeShortcutAdd');
    }
  }

  // Return whether the shortcut was added or not
  return added;
}

async function addHomeShortcut(origin: PopupDialogOrigin) {
  // Attempt to add the home shortcut
  try {
    track('AddHomeScreenConfirm', {});
    await platform.createShortcutAsync({
      analytics: {
        feature: Feature.HOME_SHORTCUT._,
        subFeature: Feature.HOME_SHORTCUT.ADD,
        origin,
      },
      payload: {
        $channel: 'HOMESCREEN',
        feature: Feature.HOME_SHORTCUT._,
        $subFeature: Feature.HOME_SHORTCUT.LAUNCH,
      },
    });
    return true;
  } catch (e) {
    captureGenericError(`home screen action failed:`, e);
    return false;
  }
}
